import React, { useState, useEffect } from 'react';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  className?: string;
}

function Image({
  src,
  alt = "Image Name",
  className = "",
  ...props
}: ImageProps) {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  const normalizeImageSrc = (rawSrc?: string): string => {
    if (!rawSrc || rawSrc.trim() === '') {
      return '';
    }

    const cleanedSrc = rawSrc.trim();

    // Already absolute or inline sources
    if (
      cleanedSrc.startsWith('http://') ||
      cleanedSrc.startsWith('https://') ||
      cleanedSrc.startsWith('data:') ||
      cleanedSrc.startsWith('blob:')
    ) {
      return cleanedSrc;
    }

    // Frontend static assets
    if (cleanedSrc.startsWith('/assets/')) {
      return cleanedSrc;
    }

    // Backend storage/media relative paths
    if (
      cleanedSrc.startsWith('/storage/') ||
      cleanedSrc.startsWith('storage/') ||
      cleanedSrc.startsWith('/media/') ||
      cleanedSrc.startsWith('media/')
    ) {
      const pathWithoutLeadingSlash = cleanedSrc.replace(/^\/+/, '');
      if (pathWithoutLeadingSlash.startsWith('storage/')) {
        return `${apiBaseUrl}/${pathWithoutLeadingSlash}`;
      }
      return `${apiBaseUrl}/storage/${pathWithoutLeadingSlash}`;
    }

    // Other absolute paths should resolve against API host (e.g. /api/v1/media/{id}/view)
    if (cleanedSrc.startsWith('/')) {
      return `${apiBaseUrl}${cleanedSrc}`;
    }

    // Raw relative backend paths (e.g. events/cover-images/xyz.jpg) should be treated as storage files.
    return `${apiBaseUrl}/storage/${cleanedSrc.replace(/^\/+/, '')}`;
  };

  // Create a more reliable placeholder SVG with proper encoding
  // Using a simple gray rectangle that will always render
  const createPlaceholder = (width = 400, height = 400) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect fill="#e5e7eb" width="100%" height="100%"/><text fill="#9ca3af" font-family="sans-serif" font-size="16" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">No Image</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };
  
  const placeholderImage = createPlaceholder();
  const [imageSrc, setImageSrc] = useState<string>(normalizeImageSrc(src) || placeholderImage);
  const [hasError, setHasError] = useState(false);

  // Update imageSrc when src prop changes
  useEffect(() => {
    const normalizedSrc = normalizeImageSrc(src);

    if (!normalizedSrc) {
      setHasError(false);
      setImageSrc(placeholderImage);
      return;
    }

    if (normalizedSrc !== imageSrc) {
      // Reset error state whenever the incoming source actually changes.
      setHasError(false);
      setImageSrc(normalizedSrc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    // Only replace if it's not already the placeholder to avoid infinite loop
    if (!hasError && target.src !== placeholderImage && !target.src.includes('data:image/svg+xml')) {
      setHasError(true);
      // Force update the src to placeholder
      const newSrc = placeholderImage;
      setImageSrc(newSrc);
      // Use setTimeout to ensure the state update is processed
      setTimeout(() => {
        target.src = newSrc;
      }, 0);
    }
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={handleError}
      style={{ display: 'block' }}
      {...props}
    />
  );
}

export default Image;